import { pilot } from "./core/autopilot";
import { Game, type GameSnapshot } from "./core/game";
import type { Direction } from "./core/types";
import { Hud, bindControls } from "./render/hud";
import { createScene } from "./render/scene";
import { bindTouchControls } from "./render/touch";

/**
 * Entry point: build the game, the scene and the loop, then expose a small
 * control surface on `window` so Playwright can drive and inspect a real
 * running instance rather than a stand-in.
 */

/** What a driver can ask the pilot for. */
export interface PilotRequest {
  /** Bank 5h Quota Resets until slop are out, then chase them down. */
  readonly hunt?: boolean;
  /** Hold this many Story Points back while `unfinished` is true. */
  readonly reserve?: number;
  /** There is still hunting to do, so do not end the level yet. */
  readonly unfinished?: boolean;
}

export interface AstroTestApi {
  /** The current game state, same object the renderer reads. */
  state(): GameSnapshot;
  /**
   * What the built-in pilot would press right now, for a driver to act on.
   *
   * `hunt` swaps in the slop-hunting policy: bank 5h Quota Resets until slop
   * are out, then chase the frightened ones down across the whole deck.
   */
  suggestion(opts?: PilotRequest): Direction | null;
  /** Speed the simulation up for recording; 1 is real time. */
  setTimeScale(scale: number): void;
  timeScale(): number;
  /** Frames rendered since load -- proof the loop is actually running. */
  frames(): number;
  /** Astronaut animation mixer state: which clip, and every clip's weight. */
  animation(): { current: string; weights: Record<string, number>; facing: number };
  /** Resolves once the models are loaded and the first frame is on screen. */
  ready: Promise<void>;
}

declare global {
  interface Window {
    __astro?: AstroTestApi;
  }
}

const FIXED_DT = 1 / 120;
const MAX_FRAME = 0.25;

const boot = async (): Promise<void> => {
  const canvas = document.querySelector<HTMLCanvasElement>("#stage");
  const hudRoot = document.querySelector<HTMLElement>("#hud");
  const loading = document.querySelector<HTMLElement>("#loading");
  if (canvas === null || hudRoot === null) throw new Error("index.html is missing #stage or #hud");

  const game = new Game({ seed: 20260919 });
  const hud = new Hud(hudRoot);
  bindControls(game);
  // Bound unconditionally, not behind a device sniff. The pad keeps itself
  // hidden until it sees a coarse pointer or a real touch, so a desktop is
  // unaffected and a hybrid device works whichever way it is picked up.
  bindTouchControls(game, canvas);

  const scene = await createScene(canvas, game, hudRoot);
  window.addEventListener("resize", () => scene.resize());
  // Mobile browsers grow and shrink the viewport as their toolbars slide away,
  // and iOS does not always follow that with a window `resize`. visualViewport
  // does fire, and without it the canvas keeps the old height and the deck
  // stretches. orientationchange lands before the new size is readable, so it
  // is deliberately deferred a frame.
  window.visualViewport?.addEventListener("resize", () => scene.resize());
  window.addEventListener("orientationchange", () => requestAnimationFrame(() => scene.resize()));

  let frames = 0;
  let timeScale = 1;
  let accumulator = 0;
  let last = performance.now();
  let firstFrame: () => void = () => {};
  const ready = new Promise<void>((resolve) => {
    firstFrame = resolve;
  });

  const loop = (now: number): void => {
    const wall = Math.min((now - last) / 1000, MAX_FRAME);
    last = now;

    // Fixed-step simulation: the ghost AI decides on tile centres, and a long
    // browser frame must not let an actor skip past one.
    accumulator += wall * timeScale;
    let steps = 0;
    while (accumulator >= FIXED_DT && steps < 240) {
      game.update(FIXED_DT);
      accumulator -= FIXED_DT;
      steps += 1;
    }

    scene.render(wall);
    hud.update(game.snapshot());
    frames += 1;
    if (frames === 2) {
      loading?.remove();
      firstFrame();
    }
    requestAnimationFrame(loop);
  };
  requestAnimationFrame(loop);

  window.__astro = {
    state: () => game.snapshot(),
    suggestion: (opts?: PilotRequest) =>
      pilot(game, {
        ...(opts?.hunt === true ? { huntSlop: true } : {}),
        ...(opts?.reserve === undefined ? {} : { reserveStoryPoints: opts.reserve }),
        ...(opts?.unfinished === undefined ? {} : { unfinishedBusiness: opts.unfinished }),
      }),
    setTimeScale: (scale: number) => {
      timeScale = Math.max(0, Math.min(8, scale));
    },
    timeScale: () => timeScale,
    frames: () => frames,
    animation: () => scene.animationState(),
    ready,
  };
};

boot().catch((error: unknown) => {
  const message = error instanceof Error ? error.message : String(error);
  document.body.insertAdjacentHTML("beforeend", `<pre class="fatal" data-testid="fatal">${message}</pre>`);
  throw error;
});
